import React, { useEffect, useRef, useState } from "react";
import { gsap } from "gsap";

/**
 * Arcade - Dino-like runner with SVG cactus, birds (two heights) and sounds (WebAudio)
 * + Progressive speed-up: +2 px/s every 10s with notch SFX
 * + Scaled bird heights for smaller playfields
 * + Transparent background theme (light obstacles for green pages)
 * + KO sprite support via `characterSrcGameOver`
 *
 * How to use
 * 1) npm i gsap
 * 2) Place your png images and pass them as props
 * 3) <Arcade characterSrc={image} characterSrcGameOver={imageKO} height={250} speed={400} sfxEnabled />
 */
export default function Arcade({
	characterSrc = null,
	characterSrcGameOver = null,
	height = 250,
	speed = 400,
	sfxEnabled = true,
}) {
	// --- Refs
	const containerRef = useRef(null);
	const groundRef = useRef(null);
	const groundTweenRef = useRef(null);
	const playerWrapRef = useRef(null);
	const playerImgRef = useRef(null);
	const scoreElRef = useRef(null);

	const playingRef = useRef(false);
	const isJumpingRef = useRef(false);
	const obstacleTweensRef = useRef([]);
	const flapTweensRef = useRef([]);
	const spawnDCRef = useRef(null);
	const speedUpDCRef = useRef(null);
	const tickerCbRef = useRef(null);
	const playerBobTweenRef = useRef(null);

	const scoreRef = useRef(0);
	const baseSpeedRef = useRef(speed);
	const currentSpeedRef = useRef(speed);

	const [state, setState] = useState("ready"); // ready | playing | gameover
	const [muted, setMuted] = useState(!sfxEnabled);

	// --- THEME (transparent bg, light visuals for green page bg)
	const colors = {
		ground: "\n#eceff1", // very light gray line/tiles
		cactus: "#ffffff", // white cactus
		bird: "#f7f7f7", // near-white birds
		birdEye: "#e0e0e0", // subtle eye
		hudText: "#f8f9fa", // light HUD on dark/green pages
		overlayBg: "rgba(44, 83, 0, 1)",
	};

	// --- Audio (WebAudio beeps)
	const audioCtxRef = useRef(null);
	const ensureAudio = () => {
		if (!audioCtxRef.current) {
			const ctx = new (window.AudioContext || window.webkitAudioContext)();
			audioCtxRef.current = ctx;
		}
		if (audioCtxRef.current?.state === "suspended") audioCtxRef.current.resume();
	};

	const beep = (freq = 440, duration = 0.08, type = "sine", gain = 0.06) => {
		if (muted) return;
		ensureAudio();
		const ctx = audioCtxRef.current;
		if (!ctx) return;
		const t0 = ctx.currentTime;
		const osc = ctx.createOscillator();
		const g = ctx.createGain();
		osc.type = type;
		osc.frequency.setValueAtTime(freq, t0);
		g.gain.setValueAtTime(0, t0);
		g.gain.linearRampToValueAtTime(gain, t0 + 0.005);
		g.gain.exponentialRampToValueAtTime(0.0001, t0 + duration);
		osc.connect(g).connect(ctx.destination);
		osc.start(t0);
		osc.stop(t0 + duration + 0.02);
	};

	const sfx = {
		jump: () => beep(740, 0.13, "square", 0.08),
		score: () => beep(1200, 0.05, "triangle", 0.06),
		death: () => {
			beep(220, 0.2, "sawtooth", 0.09);
			setTimeout(() => beep(160, 0.22, "sine", 0.07), 70);
		},
		flap: () => beep(900, 0.02, "square", 0.03),
		notch: () => {
			beep(1200, 0.06, "square", 0.06);
			setTimeout(() => beep(1600, 0.06, "square", 0.05), 70);
		},
	};

	// --- Helpers
	const vw = () => containerRef.current?.clientWidth ?? 0;
	const groundY = 12; // px from bottom

	const ratio = () => currentSpeedRef.current / baseSpeedRef.current; // timeScale factor

	const setScore = (v) => {
		scoreRef.current = v;
		if (scoreElRef.current)
			scoreElRef.current.textContent = String(Math.floor(v)).padStart(5, "0");
	};

	const resetWorld = () => {
		gsap.killTweensOf(".mascotte-obstacle");
		obstacleTweensRef.current.forEach((t) => t.kill());
		flapTweensRef.current.forEach((t) => t.kill());
		obstacleTweensRef.current = [];
		flapTweensRef.current = [];
		spawnDCRef.current?.kill?.();
		spawnDCRef.current = null;
		speedUpDCRef.current?.kill?.();
		speedUpDCRef.current = null;

		const el = containerRef.current;
		if (el) el.querySelectorAll(".mascotte-obstacle").forEach((n) => n.remove());

		// stop running bob
		playerBobTweenRef.current?.kill();
		playerBobTweenRef.current = null;

		gsap.set(playerWrapRef.current, { y: 0, x: 40, clearProps: "scaleY" });
		gsap.set(playerImgRef.current, { rotation: 0, y: 0 });
		setScore(0);

		// reset speed
		baseSpeedRef.current = speed;
		currentSpeedRef.current = speed;
		// kill parallax tween if any
		groundTweenRef.current?.kill();
		groundTweenRef.current = null;
	};

	// --- Parallax helpers (single definition)
	const startParallax = () => {
		const patternSize = 24;
		gsap.set(groundRef.current, { backgroundSize: `${patternSize}px 2px`, backgroundPositionX: 0 });
		const dur = patternSize / baseSpeedRef.current; // base duration
		groundTweenRef.current = gsap.to(groundRef.current, {
			backgroundPositionX: `-=${patternSize}`,
			duration: dur,
			ease: "none",
			repeat: -1,
		});
		// apply current ratio in case speed changed before start
		groundTweenRef.current.timeScale(ratio());
	};
	const stopParallax = () => {
		groundTweenRef.current?.kill();
		groundTweenRef.current = null;
	};

	const setWorldSpeed = (newSpeed) => {
		currentSpeedRef.current = newSpeed;
		const r = ratio();
		// scale parallax & current obstacles
		groundTweenRef.current?.timeScale(r);
		obstacleTweensRef.current.forEach((t) => t.timeScale(r));
	};

	const scheduleSpeedUp = () => {
		speedUpDCRef.current = gsap.delayedCall(10, () => {
			if (!playingRef.current) return; // safety
			const next = currentSpeedRef.current + 2; // +2 px/s each 10s
			setWorldSpeed(next);
			sfx.notch();
			scheduleSpeedUp(); // recurse
		});
	};

	const jump = () => {
		if (!playingRef.current || isJumpingRef.current) return;
		isJumpingRef.current = true;
		// Scale jump height with component height for smaller playgrounds
		const jumpHeight = Math.max(70, height * 0.50);
		sfx.jump();

		const tl = gsap.timeline({ defaults: { ease: "power2.out" } });
		tl.to(playerWrapRef.current, { y: -jumpHeight, duration: 0.3 })
			.to(playerWrapRef.current, { y: 0, duration: 0.42, ease: "power2.in" })
			.add(() => (isJumpingRef.current = false));
	};

	// --- SVG templates (light theme)
	const cactusSVG = (scale = 1) => {
		const w = 28 * scale;
		const h = 48 * scale;
		const color = colors.cactus;
		return `<svg width="${w}" height="${h}" viewBox="0 0 28 48" xmlns="http://www.w3.org/2000/svg">
	  <g fill="${color}" stroke="rgba(0,0,0,.08)" stroke-width="0.5">
		<rect x="12" y="6" width="4" height="34" rx="1"/>
		<rect x="2" y="18" width="6" height="3" rx="1"/>
		<rect x="4" y="18" width="4" height="14" rx="1"/>
		<rect x="20" y="14" width="6" height="3" rx="1"/>
		<rect x="20" y="14" width="4" height="16" rx="1"/>
		<rect x="8" y="40" width="12" height="3"/>
	  </g>
	</svg>`;
	};

	const birdSVG = (w = 38, h = 28) => {
		return `<svg width="${w}" height="${h}" viewBox="0 0 38 28" xmlns="http://www.w3.org/2000/svg">
	  <g fill="${colors.bird}" stroke="rgba(0,0,0,.06)" stroke-width="0.5">
		<path d="M2 16c6-2 10-2 16 0 5 2 10 2 16 0 0 0-1 5-6 6-5 1-9-1-12 0-3 1-7-1-10-6z"/>
		<g id="wingL"><path d="M8 10c-4-2-6-4-7-9 5 2 9 5 12 9z"/></g>
		<g id="wingR" transform="translate(30,0) scale(-1,1)"><path d="M8 10c-4-2-6-4-7-9 5 2 9 5 12 9z"/></g>
		<circle cx="26" cy="10" r="1.2" fill="${colors.birdEye}"/>
	  </g>
	</svg>`;
	};

	const spawnObstacle = () => {
		if (!playingRef.current) return;
		const el = document.createElement("div");
		el.className = "mascotte-obstacle";
		el.style.position = "absolute";
		el.style.right = "-80px";
		el.style.bottom = `${groundY + 2}px`;
		containerRef.current.appendChild(el);

		const isBird = Math.random() < 0.28; // 28% birds

		if (isBird) {
			// Heights now scale with component height so high birds stay reachable on small canvases
			const lowH = Math.max(26, Math.round(height * 0.22));
			const highH = Math.max(lowH + 12, Math.round(height * 0.36));
			const heights = [lowH, highH];
			const flightH = heights[Math.floor(Math.random() * heights.length)];
			el.style.bottom = `${groundY + flightH}px`;
			el.innerHTML = birdSVG();

			// flap animation: rotate wings around their roots
			const svg = el.querySelector("svg");
			const wingL = svg?.getElementById("wingL");
			const wingR = svg?.getElementById("wingR");
			if (wingL && wingR) {
				const tlFlap = gsap.timeline({ repeat: -1, yoyo: true });
				tlFlap.to(wingL, { rotation: -22, svgOrigin: "8 10", duration: 0.12, ease: "sine.inOut" });
				tlFlap.to(wingR, { rotation: 22, svgOrigin: "30 10", duration: 0.12, ease: "sine.inOut" }, "<");
				// flap SFX driven by timeline repeat (no lingering delayedCall)
				tlFlap.eventCallback("onRepeat", () => { if (playingRef.current && !muted) sfx.flap(); });
				el._flapTl = tlFlap;
				flapTweensRef.current.push(tlFlap);
			}
		} else {
			const scale = gsap.utils.random(0.9, 1.25);
			el.innerHTML = cactusSVG(scale);
		}

		const dist = vw() + 120;
		const dur = dist / baseSpeedRef.current; // base duration
		const tween = gsap.fromTo(
			el,
			{ x: 0 },
			{
				x: -dist,
				duration: dur,
				ease: "none",
				onUpdate: () => {
					// collision detection with player wrapper
					const p = playerWrapRef.current?.getBoundingClientRect();
					const b = el.getBoundingClientRect();
					if (!p) return;
					const overlap = !(p.right < b.left || p.left > b.right || p.bottom < b.top || p.top > b.bottom);
					if (overlap) {
						el._flapTl?.kill();
						endGame();
					}
				},
				onComplete: () => {
					if (el._flapTl) { el._flapTl.kill(); el._flapTl = null; }
					if (playingRef.current) {
						setScore(scoreRef.current + 20);
						sfx.score();
					}
					el.remove();
				},
			}
		);

		// apply timeScale according to current world speed
		tween.timeScale(ratio());

		obstacleTweensRef.current.push(tween);

		const nextDelay = gsap.utils.random(0.6, 1.4);
		spawnDCRef.current = gsap.delayedCall(nextDelay, spawnObstacle);
	};

	const startGame = () => {
		ensureAudio();
		resetWorld();
		setState("playing");
		playingRef.current = true;

		// initialize base/current speeds, start parallax and schedule speed-ups
		baseSpeedRef.current = speed;
		currentSpeedRef.current = speed;
		startParallax();
		setWorldSpeed(currentSpeedRef.current);
		scheduleSpeedUp();

		// tiny running bob on sprite (inner img only)
		playerBobTweenRef.current?.kill();
		playerBobTweenRef.current = gsap.to(playerImgRef.current, {
			y: "-=6",
			duration: 0.28,
			ease: "sine.inOut",
			yoyo: true,
			repeat: -1,
		});

		// time score ticker
		tickerCbRef.current = () => {
			if (!playingRef.current) return;
			setScore(scoreRef.current + 0.15);
		};
		gsap.ticker.add(tickerCbRef.current);

		spawnObstacle();
	};

	const endGame = () => {
		if (!playingRef.current) return;
		playingRef.current = false;
		setState("gameover");

		sfx.death();

		speedUpDCRef.current?.kill?.();
		obstacleTweensRef.current.forEach((t) => t.pause());
		flapTweensRef.current.forEach((t) => t.kill());
		flapTweensRef.current = [];
		stopParallax();

		// stop running bob to avoid bouncing while KO
		playerBobTweenRef.current?.kill();
		playerBobTweenRef.current = null;

		// small squash
		gsap.to(playerWrapRef.current, { y: 0, duration: 0.08 });
		gsap.to(playerWrapRef.current, { scaleY: 0.78, duration: 0.08, yoyo: true, repeat: 1 });
	};

	// Controls
	useEffect(() => {
		const onKey = (e) => {
			if (e.code === "Space" || e.code === "ArrowUp") {
				e.preventDefault();
				if (state === "ready" || state === "gameover") startGame();
				else jump();
			}
			if (e.code === "Enter" && state === "gameover") startGame();
			if (e.code === "KeyM") setMuted((m) => !m);
		};
		const onTap = () => {
			if (state === "ready" || state === "gameover") startGame();
			else jump();
		};
		window.addEventListener("keydown", onKey);
		containerRef.current?.addEventListener("pointerdown", onTap);
		return () => {
			window.removeEventListener("keydown", onKey);
			containerRef.current?.removeEventListener("pointerdown", onTap);
		};
	}, [state]);

	// Cleanup
	useEffect(() => () => {
		gsap.ticker.remove(tickerCbRef.current);
		resetWorld();
	}, []);

	// --- Sprite selection (swap on game over if provided)
	const imgSrc = state === "gameover" && characterSrcGameOver ? characterSrcGameOver : characterSrc;

	return (
		<div ref={containerRef} style={{ position: "relative", width: "100%", height: `${height}px`, maxWidth: "100%", margin: "0 auto", userSelect: "none", background: "transparent" }}>
			{/* ground (very light) */}
			<div ref={groundRef} style={{ position: "absolute", left: 0, right: 0, bottom: `${groundY}px`, height: 2, backgroundImage: `repeating-linear-gradient(90deg,${colors.ground} 0 12px,transparent 12px 24px)` }} />

			{/* player (smaller sprite area to match reduced game box) */}
			<div ref={playerWrapRef} style={{ position: "absolute", left: 24, bottom: `${groundY + 6}px`, width: 56, height: 70 }}>
				<img ref={playerImgRef} src={imgSrc} alt="mascotte" draggable={false} style={{ position: "absolute", bottom: 0, left: 0, height: "100%", filter: "drop-shadow(0 2px 0 rgba(0,0,0,.15))" }} />
			</div>

			{/* HUD (light text for dark/green backgrounds) */}
			<div style={{ position: "absolute", top: 2, left: 8, right: 8, display: "flex", justifyContent: "space-between", alignItems: "center", fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace", fontSize: 16, color: colors.hudText }}>
				<div style={{ opacity: .85, left: 32, position: "relative" }}>{state === "playing" ? "RUN" : state === "gameover" ? "GAME OVER" : "READY"}</div>
				<div ref={scoreElRef}>00000</div>
			</div>

			{/* Controls / mute */}
			<button onClick={() => setMuted(m => !m)} style={{ position: "absolute", top: -2, left: 0, fontSize: 11, padding: "4px 6px", border: "1px solid rgba(255,255,255,.6)", color: colors.hudText, borderRadius: 6, background: "rgba(255,255,255,.08)", cursor: "pointer" }}>{muted ? "🔇" : "🔊"}</button>

			{/* overlay */}
			{state !== "playing" && (
				<div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", pointerEvents: "none" }}>
					<div style={{ background: colors.overlayBg, backdropFilter: "blur(2px)", fontSize: "1.5rem", borderRadius: 16, padding: 12, boxShadow: "0 8px 20px rgba(0,0,0,.08)", textAlign: "center" }}>
						<div style={{ fontWeight: 700, marginBottom: 0 }}>{state === "gameover" ? "Game over" : "Pronto a correre?"}</div>
						<div style={{ fontSize: "1rem" }}>Press <b>Space</b> / tap to {state === "gameover" ? "restart" : "start"} – <b>M</b> to mute</div>
					</div>
				</div>
			)}
		</div>
	);
}
